/** @jsxImportSource @emotion/react */
import React, { useCallback, useState } from "react";
import SingleDownArrow from "../../../img/trade-interface/single_downarrow.svg";
import { ReactComponent as LargeSelectPlugIcon } from "../../../img/add_token_symbol_selector.svg";
import "../../../pages/Trade/index.css";
import { Token } from "../../../store/global/TokenEnum";
import { GlobalStore } from "../../../store/global/GlobalStore";
import {
  TokenSelectModal,
  TokenSelectMode,
} from "../../Modal/instance/SelectTokenModal/TokenSelectModal";
import { Amount } from "../../../types/amount";
import clsx from "clsx";
import percentRound from "percent-round";
import { SelectTokensContainer } from "./elements/SelectTokensContainer";
import { Theme } from "../../../styles/theme";
import { SymbolSelector } from "./elements/SymbolSelector";
import { SymbolSelectorContainer } from "./elements/SymbolSelectorContainer";
import { SelectedToken } from "./elements/SelectedToken";

export interface TokenSectionProps {
  symbol: Token | undefined;
  tokenIcon: string;
  tokenSelectMode: TokenSelectMode;
  counterParty: "maker" | "taker";
  isSliderApplicable: boolean;
  theme: Theme;
}

export const TokenInputSection: React.FC<TokenSectionProps> = ({
  symbol,
  tokenIcon,
  tokenSelectMode,
  counterParty,
  isSliderApplicable,
  theme,
}) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const handleClick = useCallback(() => {
    setModalOpen(true);
  }, []);
  const renderLabel = () => {
    if (!symbol) {
      return (
        <SelectTokensContainer>
          <LargeSelectPlugIcon className="me-2" />
          Select Tokens
        </SelectTokensContainer>
      );
    } else {
      return (
        <>
          <img width="32" height="32" src={tokenIcon} alt={symbol} className={"icon"} />
          <SelectedToken data-testid={`token-input-section-symbol-${counterParty}-text`}>
            {symbol}
          </SelectedToken>
          {tokenSelectMode !== "none" && <img src={SingleDownArrow} alt="single-down-arrow" />}
        </>
      );
    }
  };
  const handleOnTokensSelected = useCallback(
    (tokens: Array<Token>) => {
      GlobalStore.update(s => {
        const newPercentages: Array<number> = new Array(tokens.length).fill(100 / tokens.length);
        const roundedPercentages = percentRound(newPercentages);
        const newTokens = tokens.map((entry, index) => {
          return {
            token: entry,
            amount: Amount.ZERO,
            pct: roundedPercentages[index],
            isLocked: false,
          };
        });
        if (counterParty === "maker") {
          s.makerTokens = newTokens;
        } else {
          s.isRequestingQuote = false;
          s.quote = undefined;
          s.takerTokens = newTokens;
        }
      });
    },
    [counterParty]
  );
  return (
    <SymbolSelectorContainer
      className={clsx({
        "col-md-auto col-auto mw-152": isSliderApplicable,
        "col-5 col-12": !isSliderApplicable && !symbol,
      })}
    >
      <TokenSelectModal
        counterParty={counterParty}
        isOpen={modalOpen}
        onSelected={tokens => handleOnTokensSelected(tokens)}
        tokenSelectMode={symbol ? "single" : tokenSelectMode}
        currentSymbol={symbol}
        onClose={() => {
          setModalOpen(false);
        }}
      />
      <SymbolSelector theme={theme}>
        <span
          className="pointer d-flex align-items-center"
          onClick={handleClick}
          data-testid={`token-input-section-${!symbol ? "empty" : "nonEmpty"}-label`}
        >
          {renderLabel()}
        </span>
      </SymbolSelector>
    </SymbolSelectorContainer>
  );
};
